import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Loader } from "@mantine/core";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { useActionCenter, useSelectDecision } from "@/hooks/tasks";
import { SuggestedDecision } from "./suggested-decision";
import { Actions } from "./actions";
import { ActionCentreSkeleton } from "./action-centre-skeleton";
/** Action centre panel showing AI-generated decision suggestions for a reminder. */
export function ActionCentre(_ref) {
  var _data$suggestion$user, _data$suggestion, _suggestion$decisions, _suggestion$decisions2, _suggestion$decisions3, _confirmedDecision$ac;
  var reminderId = _ref.reminderId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var queryClient = useQueryClient();
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient$pa, _s$selectedPatient;
    return (_s$selectedPatient$pa = (_s$selectedPatient = s.selectedPatient) === null || _s$selectedPatient === void 0 ? void 0 : _s$selectedPatient.patient_uid) !== null && _s$selectedPatient$pa !== void 0 ? _s$selectedPatient$pa : null;
  });
  var _useActionCenter = useActionCenter(patientUid, reminderId),
    data = _useActionCenter.data,
    isLoading = _useActionCenter.isLoading;
  var _useSelectDecision = useSelectDecision(patientUid, reminderId),
    clearDecision = _useSelectDecision.mutate,
    isClearing = _useSelectDecision.isLoading;
  var serverDecisionId = (_data$suggestion$user = data === null || data === void 0 || (_data$suggestion = data.suggestion) === null || _data$suggestion === void 0 ? void 0 : _data$suggestion.user_decision_id) !== null && _data$suggestion$user !== void 0 ? _data$suggestion$user : null;
  var _useState = useState(undefined),
    localDecisionId = _useState[0],
    setLocalDecisionId = _useState[1];
  var confirmedDecisionId = localDecisionId === undefined ? serverDecisionId : localDecisionId;
  var _useState2 = useState(false),
    overrideActive = _useState2[0],
    setOverrideActive = _useState2[1];
  var handleOverride = function handleOverride() {
    setOverrideActive(true);
    queryClient.invalidateQueries(["action-center", patientUid, reminderId]);
  };
  var handleBackToSuggested = function handleBackToSuggested() {
    clearDecision({
      decision_id: null,
      override: false
    }, {
      onSuccess: function onSuccess() {
        setOverrideActive(false);
        setLocalDecisionId(null);
        queryClient.invalidateQueries(["action-center", patientUid, reminderId]);
      }
    });
  };
  if (isLoading && patientUid) return /*#__PURE__*/React.createElement(ActionCentreSkeleton, null);
  if (!data) return null;
  var suggestion = data.suggestion;
  var hasDecisions = !!suggestion && ((_suggestion$decisions = (_suggestion$decisions2 = suggestion.decisions) === null || _suggestion$decisions2 === void 0 ? void 0 : _suggestion$decisions2.length) !== null && _suggestion$decisions !== void 0 ? _suggestion$decisions : 0) > 0;
  var confirmedDecision = suggestion === null || suggestion === void 0 || (_suggestion$decisions3 = suggestion.decisions) === null || _suggestion$decisions3 === void 0 ? void 0 : _suggestion$decisions3.find(function (d) {
    return d.id === confirmedDecisionId;
  });
  var isConfirmed = confirmedDecision !== undefined;
  var isOverridden = overrideActive || !!(suggestion !== null && suggestion !== void 0 && suggestion.override_selected) || !hasDecisions;
  var isOverrideMode = isOverridden && !isConfirmed;
  var isActionsActive = isConfirmed || isOverridden;
  var isGenerating = (suggestion === null || suggestion === void 0 ? void 0 : suggestion.status) === "generating";
  var isFailed = (suggestion === null || suggestion === void 0 ? void 0 : suggestion.status) === "failed";
  var showAiraLogo = !!suggestion && !overrideActive && !suggestion.override_selected;
  var handleChangeDecision = function handleChangeDecision() {
    if (isOverrideMode) {
      handleBackToSuggested();
      return;
    }
    setLocalDecisionId(null);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "border-t border-sds-base-default"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3"
  }, showAiraLogo && /*#__PURE__*/React.createElement(Image, {
    src: "/assets/icons/aira-logo.svg",
    alt: "",
    width: 20,
    height: 20,
    className: "h-5 w-5 flex-shrink-0"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-base-strong text-sds-base-default"
  }, t("label.action_centre", {
    defaultValue: "Action centre"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "border-t border-sds-base-default"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-style-body-base text-md text-sds-base-secondary"
  }, hasDecisions ? t("label.action_centre_helper", {
    defaultValue: "Review AIRA's suggestion, then confirm or choose your own actions."
  }) : t("label.action_centre_helper_actions_only", {
    defaultValue: "Choose the actions you want to perform for this task."
  })), isGenerating ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center gap-3 py-6"
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "sm",
    color: "gray"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-base text-sds-base-secondary"
  }, t("label.action_centre_generating", {
    defaultValue: "Generating suggestion…"
  }))) : isFailed ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center py-6"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-base text-sds-urgent-default"
  }, t("label.action_centre_generation_failed", {
    defaultValue: "No AIRA suggestion. Try again."
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, suggestion && hasDecisions && /*#__PURE__*/React.createElement(SuggestedDecision, {
    suggestion: suggestion,
    confirmedDecisionId: confirmedDecisionId,
    isOverridden: isOverrideMode,
    patientUid: patientUid,
    reminderId: reminderId,
    onConfirm: setLocalDecisionId,
    onChangeDecision: handleChangeDecision,
    onOverride: handleOverride
  }), /*#__PURE__*/React.createElement(Actions, {
    isActive: isActionsActive,
    actions: (_confirmedDecision$ac = confirmedDecision === null || confirmedDecision === void 0 ? void 0 : confirmedDecision.actions) !== null && _confirmedDecision$ac !== void 0 ? _confirmedDecision$ac : [],
    patientUid: patientUid,
    reminderId: reminderId,
    isOverride: isOverrideMode,
    onBack: handleBackToSuggested,
    isBackLoading: isClearing,
    canGoBack: hasDecisions,
    stepNumber: hasDecisions ? 2 : 1
  }))));
}